import axios from 'axios';
import { API_SOURCES } from '@/config/apiSources.js';

// databridge-api on the phila.gov API gateway, called directly. The gateway recognizes
// the deployed origins (li.phila.gov and the dev CloudFront), so builds send no client
// id; the local dev server is unknown to it and sends VUE_APP_GATEWAY_CLIENT_ID from
// .env.local (axios drops the param when it's unset).
// Ported from vue3-atlas src/util/databridge.js - keep the two in step.
export const DATABRIDGE_URL = 'https://api-prod.phila.gov/databridge-api/v1/get';
const GATEWAY_CLIENT_ID = process.env.VUE_APP_GATEWAY_CLIENT_ID;

// the app's other data endpoints: the direct-carto fallback and the AIS address search
export const CARTO_ROOT = 'https://phl.carto.com/api/v2/sql';
export const AIS_ROOT = 'https://api-prod.phila.gov/ais/v1/search';

// AIS on the same gateway: GET `${AIS_ROOT}/<address or OPA number>`. cache_origin
// partitions the gateway's response cache by origin - it echoes the request Origin into
// Access-Control-Allow-Origin but its cache ignores the Vary: Origin it declares, so an
// entry cached for one origin CORS-blocks every other origin for the cache TTL
export const aisAxios = axios.create({
  baseURL: AIS_ROOT,
  params: { client_id: GATEWAY_CLIENT_ID, cache_origin: window.location.hostname },
});

// AIS autocomplete on the same gateway: the addresses AIS would complete the typed text
// to, for a suggestions list. Fewer than 3 characters is a 422, so it isn't asked; any
// failure yields no suggestions, since they are optional
const AIS_AUTOCOMPLETE_URL = 'https://api-prod.phila.gov/ais-autocomplete/v1/autocomplete';
export async function fetchAddressSuggestions(query) {
  if (!query || query.length < 3) {
    return [];
  }
  try {
    const response = await axios.get(AIS_AUTOCOMPLETE_URL, {
      params: { q: query, client_id: GATEWAY_CLIENT_ID, cache_origin: window.location.hostname },
    });
    const results = response.data && response.data.results;
    return results && results.addresses ? results.addresses.map((a) => a.address) : [];
  } catch {
    return [];
  }
}

// databridge serializes timestamps as real UTC (since 2026-10-23), spelled two ways:
// '2026-09-02T13:30:00+00:00' on table= (PostgREST) and '2026-09-02T13:30:00.000Z' on
// sql= (Carto V3). Both are rewritten to carto's spelling - a trailing Z, no milliseconds -
// so every downstream date check sees what it always saw; the instant is not changed. A bare
// reading with no zone at all ('2026-09-02T09:30:00', a table the gateway hasn't converted)
// is still the Philadelphia clock, read as local time and relabeled to real UTC.
const UTC_TIMESTAMP = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d{3})?(?:Z|\+00:00)$/;
const LOCAL_CLOCK_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
function normalizeTimestamps(properties) {
  for (const key of Object.keys(properties)) {
    const value = properties[key];
    if (typeof value !== 'string') {
      continue;
    }
    const utc = value.match(UTC_TIMESTAMP);
    if (utc) {
      properties[key] = `${utc[1]}Z`;
    } else if (LOCAL_CLOCK_TIMESTAMP.test(value)) {
      properties[key] = new Date(value).toISOString().replace('.000Z', 'Z');
    }
  }
  return properties;
}

// fetches rows for a dataset through its configured source: databridge first (per the
// named apiSources switch), falling back LOUDLY to direct carto when databridge fails -
// gateway hiccups degrade instead of emptying pages, without silently masking outages.
// sql is one string when the same statement runs on both transports, or
// { databridge, carto } when their geometry columns force different statements
export async function fetchRowsWithFallback(sourceKey, sql) {
  const databridgeSql = typeof sql === 'string' ? sql : sql.databridge;
  const cartoSql = typeof sql === 'string' ? sql : sql.carto;
  if (API_SOURCES[sourceKey] === 'databridge') {
    try {
      const data = await fetchDatabridgeRows(databridgeSql);
      if (data) {
        return data;
      }
    } catch {
      // fall through to carto below
    }
    console.warn(`${sourceKey} - databridge request failed, falling back to direct carto`);
  }
  const response = await axios.get(CARTO_ROOT, { params: { q: cartoSql } });
  if (response.status !== 200) {
    return null;
  }
  return response.data;
}

// fetches EVERY row of a table-style query, however many: walks the API's keyset
// pagination (append AND objectid > <last id seen> - pages come back objectid-
// ascending, gap- and duplicate-free) until a short page ends the set. The row cap
// only limits single responses, so complete sets arrive in a few pages and sort
// client-side. Falls back LOUDLY to carto, where cartoSql (the old uncapped
// statement) returns the same complete set in one response.
// withGeometry attaches each feature's GeoJSON geometry (already in 4326) to its row -
// the table response carries it natively, replacing st_astext/ST_AsGeoJSON selects
export async function fetchTableAllRows(sourceKey, { table, fields, where, maxAge, service, cartoSql, withGeometry, pageSize = 999 }) {
  if (API_SOURCES[sourceKey] === 'databridge') {
    const rows = [];
    let lastId = null;
    let failed = false;
    for (;;) {
      const cursor = `objectid > ${lastId}`;
      const pageWhere = lastId === null ? where : where ? `(${where}) AND ${cursor}` : cursor;
      const params = { table, where: pageWhere, limit: pageSize, client_id: GATEWAY_CLIENT_ID };
      if (fields) {
        // the fields param wants bare commas - 'col1, col2' reads ' col2' as a column
        params.fields = fields.replace(/\s/g, '');
      }
      if (maxAge !== undefined) {
        params.max_age = maxAge;
      }
      if (service) {
        params.service = service;
      }
      let response = null;
      try {
        response = await axios(DATABRIDGE_URL, { params });
      } catch {
        // fall through to carto below
      }
      if (!response || response.status !== 200 || !response.data.data || !response.data.data.features) {
        failed = true;
        break;
      }
      const features = response.data.data.features;
      features.forEach((f) => {
        const row = normalizeTimestamps(f.properties);
        if (withGeometry) {
          row.geometry = f.geometry;
        }
        rows.push(row);
      });
      if (features.length < pageSize) {
        return { rows };
      }
      lastId = features[features.length - 1].properties.objectid;
    }
    if (failed) {
      console.warn(`${sourceKey} - databridge request failed, falling back to direct carto`);
    }
  }
  const fallbackSql = cartoSql
    || `SELECT ${fields || '*'} FROM ${table}` + (where ? ` WHERE ${where}` : '');
  const response = await axios.get(CARTO_ROOT, { params: { q: fallbackSql } });
  if (response.status !== 200) {
    return null;
  }
  return response.data;
}

// replaces a sql ORDER BY client-side: postgres puts nulls last ascending, first descending.
// orderBy is one { column, descending } or a list of them, compared in order until one differs
function sortRows(rows, orderBy) {
  const keys = Array.isArray(orderBy) ? orderBy : [orderBy];
  rows.sort((a, b) => {
    for (const { column, descending } of keys) {
      if (a[column] === b[column]) continue;
      if (a[column] === null || a[column] === undefined) return descending ? -1 : 1;
      if (b[column] === null || b[column] === undefined) return descending ? 1 : -1;
      if (a[column] < b[column]) return descending ? 1 : -1;
      return descending ? -1 : 1;
    }
    return 0;
  });
}

// transport for a dataset's query, with the response shaped like the cartoAxios calls
// it replaces ({ data: { rows } }). Takes either a sql string (the documented sql=
// holdouts: DISTINCT projections, joins) or a table-style spec
// { table, where, orderBy, withGeometry, cartoSql } that keyset-walks the complete set
export async function fetchSqlRows(sourceKey, query) {
  const isSql = typeof query === 'string' || query.databridge;
  const data = isSql
    ? await fetchRowsWithFallback(sourceKey, query)
    : await fetchTableAllRows(sourceKey, query);
  const rows = data && data.rows ? data.rows : [];
  if (!isSql && query.orderBy) {
    sortRows(rows, query.orderBy);
  }
  return { data: { rows } };
}

// fetches from databridge-api and flattens the envelope to the Carto rows shape:
// { rows: [...] } - for attribute queries with no geometry
export async function fetchDatabridgeRows(sql) {
  const params = { sql, client_id: GATEWAY_CLIENT_ID };
  let response = null;
  try {
    response = await axios(DATABRIDGE_URL, { params });
  } catch {
    return null;
  }
  if (response.status !== 200 || !response.data.data || !response.data.data.features) {
    return null;
  }
  return { rows: response.data.data.features.map((f) => normalizeTimestamps(f.properties)) };
}

// fetches from databridge-api reshaped to a GeoJSON FeatureCollection matching the
// ArcGIS response shape - queries select ST_AsGeoJSON(ST_Transform(shape, 4326)) as geom
export async function fetchDatabridgeGeoJSON(sql) {
  const params = { sql, client_id: GATEWAY_CLIENT_ID };
  let response = null;
  try {
    response = await axios(DATABRIDGE_URL, { params });
  } catch {
    return null;
  }
  if (response.status !== 200 || !response.data.data || !response.data.data.features) {
    return null;
  }
  const features = response.data.data.features.map((f) => {
    const { geom, ...properties } = f.properties;
    let geometry = geom;
    if (geometry && geometry.type === 'MultiPolygon' && geometry.coordinates.length === 1) {
      geometry = { type: 'Polygon', coordinates: geometry.coordinates[0] };
    }
    return { type: 'Feature', id: properties.objectid, properties: properties, geometry: geometry };
  });
  return { type: 'FeatureCollection', features: features };
}
